import React, { useState, useEffect, useCallback } from "react";
import StudentSearchService from "../../_services/StudentSearchService";
import SearchToolbar from "./SearchToolbar";
import AdvancedFilters from "./AdvancedFilters";
import SearchResults from "./SearchResults";
import PasswordSearchResults from "./PasswordSearchResults";
import PaginationBar from "./PaginationBar";
import BulkMessageModal from "./BulkMessageModal";
import ExcelExportModal from "./ExcelExportModal";
import BulkUpdateModal from "./BulkUpdateModal";
import BulkStatusChangeModal from "./BulkStatusChangeModal";
import BulkPromoteModal from "./BulkPromoteModal";
import BulkAssignTagsModal from "./BulkAssignTagsModal";
import BulkAssignMentorModal from "./BulkAssignMentorModal";
import BulkAssignStaffModal from "./BulkAssignStaffModal";
import BulkAddSubBatchModal from "./BulkAddSubBatchModal";
import BulkStudentPhotosModal from "./BulkStudentPhotosModal";
import BulkHealthDetailsModal from "./BulkHealthDetailsModal";
import BulkAddDemandModal from "./BulkAddDemandModal";
import BulkIssueMaterialModal from "./BulkIssueMaterialModal";
import BulkDiscountModal from "./BulkDiscountModal";
import BulkTransferAlumniModal from "./BulkTransferAlumniModal";
import BulkIssueTcModal from "./BulkIssueTcModal";
import BulkIdCardModal from "./BulkIdCardModal";
import BulkGradeReportModal from "./BulkGradeReportModal";
import BulkStatementOfAccountModal from "./BulkStatementOfAccountModal";
import BulkReminderSlipModal from "./BulkReminderSlipModal";
import BulkUpdateTransportPlanModal from "./BulkUpdateTransportPlanModal";
import BulkChangePlacementStatusModal from "./BulkChangePlacementStatusModal";
import BulkAssignStreamsModal from "./BulkAssignStreamsModal";
import BulkAssignCourseModal from "./BulkAssignCourseModal";
import BulkDropModulesModal from "./BulkDropModulesModal";
import BulkTransferModal from "./BulkTransferModal";
import EnrollmentDrawer from "./components/EnrollmentDrawer";
import EnrollmentFormModal from "../enrollment-view/components/EnrollmentFormModal";

const StudentSearchPage: React.FC = () => {
  // Lookup data for dropdowns
  const [lookups, setLookups] = useState<any>(null);
  const [lookupsLoading, setLookupsLoading] = useState(true);

  // Search state
  const [searchParams, setSearchParams] = useState<any>({});
  const [results, setResults] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isPasswordSearchMode, setIsPasswordSearchMode] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  // Selection for bulk operations
  const [selectedEnquiryIds, setSelectedEnquiryIds] = useState<number[]>([]);

  // UI toggles
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [showBulkMessageModal, setShowBulkMessageModal] = useState(false);
  const [bulkMessageChannel, setBulkMessageChannel] = useState("sms");
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [showBulkUpdateModal, setShowBulkUpdateModal] = useState(false);
  const [showBulkStatusModal, setShowBulkStatusModal] = useState(false);
  const [showBulkPromoteModal, setShowBulkPromoteModal] = useState(false);
  const [promoteMode, setPromoteMode] = useState<"promote" | "addAnotherCourse">("promote");
  const [showBulkAssignTagsModal, setShowBulkAssignTagsModal] = useState(false);
  const [showBulkAssignMentorModal, setShowBulkAssignMentorModal] = useState(false);
  const [showBulkAssignStaffModal, setShowBulkAssignStaffModal] = useState(false);
  const [showBulkAddSubBatchModal, setShowBulkAddSubBatchModal] = useState(false);
  const [showBulkStudentPhotosModal, setShowBulkStudentPhotosModal] = useState(false);
  const [showBulkHealthDetailsModal, setShowBulkHealthDetailsModal] = useState(false);
  const [showBulkAddDemandModal, setShowBulkAddDemandModal] = useState(false);
  const [showBulkIssueMaterialModal, setShowBulkIssueMaterialModal] = useState(false);
  const [showBulkDiscountModal, setShowBulkDiscountModal] = useState(false);
  const [showBulkTransferAlumniModal, setShowBulkTransferAlumniModal] = useState(false);
  const [showBulkIssueTcModal, setShowBulkIssueTcModal] = useState(false);
  const [showBulkIdCardModal, setShowBulkIdCardModal] = useState(false);
  const [showBulkGradeReportModal, setShowBulkGradeReportModal] = useState(false);
  const [showBulkStatementOfAccountModal, setShowBulkStatementOfAccountModal] = useState(false);
  const [showBulkReminderSlipModal, setShowBulkReminderSlipModal] = useState(false);
  const [showBulkTransportPlanModal, setShowBulkTransportPlanModal] = useState(false);
  const [showBulkPlacementModal, setShowBulkPlacementModal] = useState(false);
  const [showBulkStreamsModal, setShowBulkStreamsModal] = useState(false);
  const [showAssignCourseModal, setShowAssignCourseModal] = useState(false);
  const [assignCourseMode, setAssignCourseMode] = useState<"module" | "elective">("module");
  const [showDropModulesModal, setShowDropModulesModal] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);

  // Enrollment drawer
  const [drawerEnrollmentId, setDrawerEnrollmentId] = useState<number | null>(null);
  const [drawerPhotoPath, setDrawerPhotoPath] = useState<string | null>(null);

  // Create enrollment modal
  const [createEnrollmentEnquiryId, setCreateEnrollmentEnquiryId] = useState<number | null>(null);

  // ── Load lookups on mount ──
  useEffect(() => {
    const fetchLookups = async () => {
      try {
        const resp = await StudentSearchService.getLookups();
        setLookups(resp.data?.data || null);
      } catch (err: any) {
        setError("Failed to load search form data.");
        console.error(err);
      } finally {
        setLookupsLoading(false);
      }
    };
    fetchLookups();
  }, []);

  // ── Auto-dismiss errors ──
  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 5000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  // ── Search function ──
  const executeSearch = useCallback(
    async (page: number, size: number) => {
      setLoading(true);
      setError("");
      setIsPasswordSearchMode(false);
      try {
        const resp = await StudentSearchService.search({
          ...searchParams,
          page,
          pageSize: size,
        });
        setResults(resp.data?.data || null);
        setCurrentPage(page);
        setSelectedEnquiryIds([]);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Search failed");
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [searchParams],
  );

  // ── Password Search function ──
  // Uses the same search endpoint but displays results in password view mode
  const executePasswordSearch = useCallback(
    async (page: number, size: number) => {
      setLoading(true);
      setError("");
      setIsPasswordSearchMode(true);
      try {
        const resp = await StudentSearchService.search({
          ...searchParams,
          passwordSearch: true,
          page,
          pageSize: size,
        });
        setResults(resp.data?.data || null);
        setCurrentPage(page);
        setSelectedEnquiryIds([]);
      } catch (err: any) {
        setError(err?.response?.data?.message || "Password search failed");
        console.error(err);
      } finally {
        setLoading(false);
      }
    },
    [searchParams],
  );

  // ── Handlers ──
  const handleSearch = () => {
    setCurrentPage(1);
    executeSearch(1, pageSize);
  };

  const handlePageChange = (page: number) => {
    if (isPasswordSearchMode) {
      executePasswordSearch(page, pageSize);
    } else {
      executeSearch(page, pageSize);
    }
  };

  const handlePageSizeChange = (size: number) => {
    setPageSize(size);
    if (isPasswordSearchMode) {
      executePasswordSearch(1, size);
    } else {
      executeSearch(1, size);
    }
  };

  const handleParamChange = (key: string, value: any) => {
    setSearchParams((prev: any) => ({ ...prev, [key]: value }));
  };

  const handleToggleSelect = (enquiryId: number) => {
    setSelectedEnquiryIds((prev) =>
      prev.includes(enquiryId)
        ? prev.filter((id) => id !== enquiryId)
        : [...prev, enquiryId],
    );
  };

  const handleToggleSelectAll = () => {
    if (!results?.studentList) return;
    const allIds = results.studentList.map((s: any) => s.enquiryId);
    if (selectedEnquiryIds.length === allIds.length) {
      setSelectedEnquiryIds([]);
    } else {
      setSelectedEnquiryIds(allIds);
    }
  };

  const handleBulkMessage = (channel: string) => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setBulkMessageChannel(channel);
    setShowBulkMessageModal(true);
  };

  const handleExcel = () => {
    setShowExcelModal(true);
  };

  const handleBulkUpdate = () => {
    setShowBulkUpdateModal(true);
  };

  const handleBulkStatusChange = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkStatusModal(true);
  };

  const handleBulkPromote = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setPromoteMode("promote");
    setShowBulkPromoteModal(true);
  };

  const handleBulkAddAnotherCourse = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setPromoteMode("addAnotherCourse");
    setShowBulkPromoteModal(true);
  };

  const handleBulkAssignTags = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkAssignTagsModal(true);
  };

  const handleBulkAssignMentor = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkAssignMentorModal(true);
  };

  const handleBulkAssignStaff = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkAssignStaffModal(true);
  };

  const handleBulkAddSubBatch = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkAddSubBatchModal(true);
  };

  const handleBulkStudentPhotos = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkStudentPhotosModal(true);
  };

  const handleBulkHealthDetails = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkHealthDetailsModal(true);
  };

  const handleBulkAddDemand = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkAddDemandModal(true);
  };

  const handleBulkIssueMaterial = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkIssueMaterialModal(true);
  };

  const handleBulkDiscount = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkDiscountModal(true);
  };

  const handleBulkTransferAlumni = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkTransferAlumniModal(true);
  };

  const handleBulkIssueTc = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkIssueTcModal(true);
  };

  const handleBulkIdCard = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkIdCardModal(true);
  };

  const handleBulkGradeReport = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkGradeReportModal(true);
  };

  const handleStatementOfAccount = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkStatementOfAccountModal(true);
  };

  const handleReminderSlip = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkReminderSlipModal(true);
  };

  const handleUpdateTransportPlan = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkTransportPlanModal(true);
  };

  const handleBulkChangePlacementStatus = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkPlacementModal(true);
  };

  const handleBulkAssignStreams = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowBulkStreamsModal(true);
  };

  const handleBulkAssignModules = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setAssignCourseMode("module");
    setShowAssignCourseModal(true);
  };

  const handleBulkAssignElectives = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setAssignCourseMode("elective");
    setShowAssignCourseModal(true);
  };

  const handleBulkDropModules = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowDropModulesModal(true);
  };

  const handleBulkTransfer = () => {
    if (selectedEnquiryIds.length === 0) {
      setError("Please select at least one student first.");
      return;
    }
    setShowTransferModal(true);
  };

  const handlePasswordSearch = () => {
    setCurrentPage(1);
    executePasswordSearch(1, pageSize);
  };

  // ── Render ──
  if (lookupsLoading) {
    return (
      <div style={{ padding: 40, textAlign: "center", color: "var(--text-faint)" }}>
        Loading search form...
      </div>
    );
  }

  return (
    <div style={{ padding: "20px 24px" }}>
      <h5 style={{ marginBottom: 10, fontWeight: 600 }}>Student Search</h5>

      {error && (
        <div
          style={{
            padding: "8px 14px",
            background: "var(--status-danger-bg)",
            color: "var(--status-danger)",
            borderRadius: 6,
            marginBottom: 10,
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {/* Search toolbar */}
      <SearchToolbar
        lookups={lookups}
        searchParams={searchParams}
        onParamChange={handleParamChange}
        onSearch={handleSearch}
        onToggleAdvanced={() => setShowAdvancedFilters((p) => !p)}
        showAdvancedFilters={showAdvancedFilters}
        loading={loading}
        onExcel={handleExcel}
        onBulkUpdate={handleBulkUpdate}
        onBulkStatusChange={handleBulkStatusChange}
        onBulkPromote={handleBulkPromote}
        onBulkAddAnotherCourse={handleBulkAddAnotherCourse}
        onBulkAssignTags={handleBulkAssignTags}
        onBulkAssignMentor={handleBulkAssignMentor}
        onBulkAssignStaff={handleBulkAssignStaff}
        onBulkAddSubBatch={handleBulkAddSubBatch}
        onBulkStudentPhotos={handleBulkStudentPhotos}
        onBulkHealthDetails={handleBulkHealthDetails}
        onBulkAddDemand={handleBulkAddDemand}
        onBulkIssueMaterial={handleBulkIssueMaterial}
        onBulkDiscount={handleBulkDiscount}
        onBulkTransferAlumni={handleBulkTransferAlumni}
        onBulkIssueTc={handleBulkIssueTc}
        onBulkIdCard={handleBulkIdCard}
        onBulkGradeReport={handleBulkGradeReport}
        onStatementOfAccount={handleStatementOfAccount}
        onReminderSlip={handleReminderSlip}
        onUpdateTransportPlan={handleUpdateTransportPlan}
        onBulkChangePlacementStatus={handleBulkChangePlacementStatus}
        onBulkAssignStreams={handleBulkAssignStreams}
        onBulkAssignModules={handleBulkAssignModules}
        onBulkAssignElectives={handleBulkAssignElectives}
        onBulkDropModules={handleBulkDropModules}
        onBulkTransfer={handleBulkTransfer}
        onBulkMessage={handleBulkMessage}
        onPasswordSearch={handlePasswordSearch}
        selectedCount={selectedEnquiryIds.length}
      />

      {/* Advanced filters (collapsible) */}
      {showAdvancedFilters && (
        <AdvancedFilters
          lookups={lookups}
          searchParams={searchParams}
          onParamChange={handleParamChange}
        />
      )}

      {/* Results */}
      {loading && (
        <div style={{ textAlign: "center", padding: 30, color: "goldenrod" }}>
          <i className="fa fa-spinner fa-spin fa-2x" />
          <span style={{ fontWeight: "bold", fontSize: 18, marginLeft: 10 }}>
            Loading...
          </span>
        </div>
      )}

      {!loading && results && (
        <div
          style={{
            background: "var(--surface-page)",
            borderRadius: 4,
            padding: 10,
            marginTop: 5,
          }}
        >
          {/* Pagination top */}
          <PaginationBar
            currentPage={currentPage}
            totalPages={results.pagination?.totalPages || 0}
            totalRecords={results.pagination?.resultCount || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />

          {/* Results table - conditional based on search mode */}
          {isPasswordSearchMode ? (
            <PasswordSearchResults students={results.studentList || []} />
          ) : (
            <SearchResults
              studentList={results.studentList || []}
              searchDisplayBeans={results.searchDisplayBeans || []}
              permissions={results.permissions || {}}
              enquiryEnrollmentsMap={results.enquiryEnrollmentsMap || {}}
              selectedEnquiryIds={selectedEnquiryIds}
              onToggleSelect={handleToggleSelect}
              onToggleSelectAll={handleToggleSelectAll}
              allSelected={
                results.studentList?.length > 0 &&
                selectedEnquiryIds.length === results.studentList.length
              }
              isProgramBased={lookups?.isProgramBased || false}
              onOpenDrawer={(enrollmentId: number, photoPath?: string | null) => {
                setDrawerEnrollmentId(enrollmentId);
                setDrawerPhotoPath(photoPath ?? null);
              }}
              onCreateEnrollment={(enquiryId: number) => setCreateEnrollmentEnquiryId(enquiryId)}
              onDeleteEnquiry={async (enquiryId: number) => {
                await StudentSearchService.deleteEnquiry(enquiryId);
                executeSearch(currentPage, pageSize);
              }}
            />
          )}

          {/* Pagination bottom */}
          <PaginationBar
            currentPage={currentPage}
            totalPages={results.pagination?.totalPages || 0}
            totalRecords={results.pagination?.resultCount || 0}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </div>
      )}

      {/* Bulk message modal */}
      <BulkMessageModal
        show={showBulkMessageModal}
        onHide={() => setShowBulkMessageModal(false)}
        channel={bulkMessageChannel}
        selectedEnquiryIds={selectedEnquiryIds}
        lookups={lookups}
      />

      {/* Excel export modal */}
      <ExcelExportModal
        show={showExcelModal}
        onHide={() => setShowExcelModal(false)}
        searchParams={searchParams}
        lookups={lookups}
      />

      {/* Bulk enrollment status change modal */}
      <BulkStatusChangeModal
        show={showBulkStatusModal}
        onHide={() => setShowBulkStatusModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk promote / add-another-course modal (same modal, two modes) */}
      <BulkPromoteModal
        show={showBulkPromoteModal}
        onHide={() => setShowBulkPromoteModal(false)}
        promotionType={promoteMode}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk assign tags modal */}
      <BulkAssignTagsModal
        show={showBulkAssignTagsModal}
        onHide={() => setShowBulkAssignTagsModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        tagTypes={lookups?.studentTagTypes || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk assign mentor modal */}
      <BulkAssignMentorModal
        show={showBulkAssignMentorModal}
        onHide={() => setShowBulkAssignMentorModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        mentors={lookups?.mentors || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk assign staff modal */}
      <BulkAssignStaffModal
        show={showBulkAssignStaffModal}
        onHide={() => setShowBulkAssignStaffModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        staffUsers={lookups?.followupUsers || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk add sub-batch modal */}
      <BulkAddSubBatchModal
        show={showBulkAddSubBatchModal}
        onHide={() => setShowBulkAddSubBatchModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk student photos (ZIP download) modal */}
      <BulkStudentPhotosModal
        show={showBulkStudentPhotosModal}
        onHide={() => setShowBulkStudentPhotosModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
      />

      {/* Bulk health details data-entry modal */}
      <BulkHealthDetailsModal
        show={showBulkHealthDetailsModal}
        onHide={() => setShowBulkHealthDetailsModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk add demand modal */}
      <BulkAddDemandModal
        show={showBulkAddDemandModal}
        onHide={() => setShowBulkAddDemandModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk issue material modal */}
      <BulkIssueMaterialModal
        show={showBulkIssueMaterialModal}
        onHide={() => setShowBulkIssueMaterialModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk discount modal */}
      <BulkDiscountModal
        show={showBulkDiscountModal}
        onHide={() => setShowBulkDiscountModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk transfer to alumni modal */}
      <BulkTransferAlumniModal
        show={showBulkTransferAlumniModal}
        onHide={() => setShowBulkTransferAlumniModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk issue transfer certificate modal */}
      <BulkIssueTcModal
        show={showBulkIssueTcModal}
        onHide={() => setShowBulkIssueTcModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk ID card generation modal */}
      <BulkIdCardModal
        show={showBulkIdCardModal}
        onHide={() => setShowBulkIdCardModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
      />

      {/* Bulk grade report modal */}
      <BulkGradeReportModal
        show={showBulkGradeReportModal}
        onHide={() => setShowBulkGradeReportModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
      />

      {/* Bulk statement of account modal */}
      <BulkStatementOfAccountModal
        show={showBulkStatementOfAccountModal}
        onHide={() => setShowBulkStatementOfAccountModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
      />

      {/* Bulk reminder slip modal */}
      <BulkReminderSlipModal
        show={showBulkReminderSlipModal}
        onHide={() => setShowBulkReminderSlipModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
      />

      {/* Bulk update transport plan modal */}
      <BulkUpdateTransportPlanModal
        show={showBulkTransportPlanModal}
        onHide={() => setShowBulkTransportPlanModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        transportPlans={lookups?.transportPlans || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk change placement status modal */}
      <BulkChangePlacementStatusModal
        show={showBulkPlacementModal}
        onHide={() => setShowBulkPlacementModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk assign streams modal */}
      <BulkAssignStreamsModal
        show={showBulkStreamsModal}
        onHide={() => setShowBulkStreamsModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk assign course (modules / electives) modal */}
      <BulkAssignCourseModal
        show={showAssignCourseModal}
        onHide={() => setShowAssignCourseModal(false)}
        mode={assignCourseMode}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk drop modules modal */}
      <BulkDropModulesModal
        show={showDropModulesModal}
        onHide={() => setShowDropModulesModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk transfer (batch move) modal */}
      <BulkTransferModal
        show={showTransferModal}
        onHide={() => setShowTransferModal(false)}
        selectedEnquiryIds={selectedEnquiryIds}
        enquiryEnrollmentsMap={results?.enquiryEnrollmentsMap || {}}
        studentList={results?.studentList || []}
        onSuccess={() => {
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Bulk update modal */}
      <BulkUpdateModal
        show={showBulkUpdateModal}
        onHide={() => setShowBulkUpdateModal(false)}
        searchParams={searchParams}
        onSuccess={() => {
          setShowBulkUpdateModal(false);
          if (results) executeSearch(currentPage, pageSize);
        }}
      />

      {/* Enrollment quick-view drawer */}
      <EnrollmentDrawer
        enrollmentId={drawerEnrollmentId}
        photoPath={drawerPhotoPath}
        onClose={() => {
          setDrawerEnrollmentId(null);
          setDrawerPhotoPath(null);
        }}
      />

      {/* Create enrollment modal */}
      <EnrollmentFormModal
        show={createEnrollmentEnquiryId != null}
        onHide={() => setCreateEnrollmentEnquiryId(null)}
        mode="create"
        enquiryId={createEnrollmentEnquiryId || undefined}
        onSuccess={(enrollmentId: number) => {
          setCreateEnrollmentEnquiryId(null);
          window.open(`/enrollment/view/${enrollmentId}`, "_blank");
          if (results) executeSearch(currentPage, pageSize);
        }}
      />
    </div>
  );
};

export default StudentSearchPage;
